import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { downloadJob, listJobs, uploadAndConvert } from "./api";
import { clearSession, ensureFreshSession, signIn, type Session } from "./auth";
import { loadRuntimeConfig, type RuntimeConfig } from "./config";

const jobsQueryKey = ["jobs"] as const;

export const App = () => {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bootError, setBootError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const runtime = await loadRuntimeConfig();
        setConfig(runtime);
        setSession(await ensureFreshSession(runtime));
      } catch (err) {
        setBootError(err instanceof Error ? err.message : "Failed to load config");
      }
    })();
  }, []);

  const jobsQuery = useQuery({
    queryKey: jobsQueryKey,
    queryFn: () => {
      if (!config) throw new Error("Missing config");
      return listJobs(config);
    },
    enabled: Boolean(config && session),
    refetchInterval: (query) => {
      const jobs = query.state.data ?? [];
      const active = jobs.some((job) =>
        ["awaiting_upload", "queued", "processing"].includes(job.status),
      );
      return active ? 4000 : 15_000;
    },
  });

  const signInMutation = useMutation({
    mutationFn: async () => {
      if (!config) throw new Error("Missing config");
      return signIn(config, email.trim(), password);
    },
    onSuccess: (next) => {
      setSession(next);
      setBootError(null);
      void queryClient.invalidateQueries({ queryKey: jobsQueryKey });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!config) throw new Error("Missing config");
      const jobId = await uploadAndConvert(config, file);
      return { jobId, fileName: file.name };
    },
    onSuccess: ({ jobId, fileName }) => {
      setNotice(`Queued ${fileName} (${jobId.slice(0, 8)}…). Processing with Textract.`);
      void queryClient.invalidateQueries({ queryKey: jobsQueryKey });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async (jobId: string) => {
      if (!config) throw new Error("Missing config");
      return downloadJob(config, jobId);
    },
    onSuccess: ({ downloadUrl, fileName }) => {
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = fileName;
      anchor.click();
    },
  });

  const onSignIn = (event: FormEvent) => {
    event.preventDefault();
    signInMutation.mutate();
  };

  const onUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setNotice(null);
    uploadMutation.mutate(file);
  };

  const actionError =
    signInMutation.error?.message ??
    uploadMutation.error?.message ??
    downloadMutation.error?.message ??
    (jobsQuery.error instanceof Error ? jobsQuery.error.message : null);

  if (!config) {
    return (
      <div className="app-shell">
        <h1 className="brand">Kindle Papers</h1>
        <p className="lede">{bootError ?? "Loading…"}</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app-shell">
        <h1 className="brand">Kindle Papers</h1>
        <p className="lede">Convert scanned scientific papers into Kindle-native EPUBs.</p>
        <form className="panel" onSubmit={onSignIn}>
          <h2>Sign in</h2>
          <div className="row">
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
          </div>
          <div className="row" style={{ marginTop: "1rem" }}>
            <button type="submit" disabled={signInMutation.isPending}>
              Sign in
            </button>
          </div>
          {actionError ? <p className="error">{actionError}</p> : null}
        </form>
      </div>
    );
  }

  const jobs = jobsQuery.data ?? [];
  const busy = uploadMutation.isPending || downloadMutation.isPending;

  return (
    <div className="app-shell">
      <div className="topbar">
        <div>
          <h1 className="brand">Kindle Papers</h1>
          <p className="lede">Upload a PDF, download an EPUB, sideload to your Kindle.</p>
        </div>
        <button
          type="button"
          className="secondary"
          onClick={() => {
            clearSession();
            setSession(null);
            queryClient.clear();
          }}
        >
          Sign out
        </button>
      </div>

      <section className="panel">
        <h2>Convert</h2>
        <label>
          PDF file
          <input type="file" accept="application/pdf,.pdf" onChange={onUpload} disabled={busy} />
        </label>
        {notice ? <p className="muted">{notice}</p> : null}
        {actionError ? <p className="error">{actionError}</p> : null}
      </section>

      <section className="panel">
        <h2>Recent jobs</h2>
        {jobsQuery.isLoading ? (
          <p className="muted">Loading jobs…</p>
        ) : jobs.length === 0 ? (
          <p className="muted">No conversions yet.</p>
        ) : (
          <ul className="jobs">
            {jobs.map((job) => (
              <li key={job.jobId}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <div>
                    <div className="job-title">{job.title ?? job.sourceFileName}</div>
                    <div className="muted">
                      {job.sourceFileName} · {new Date(job.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <span className={`status ${job.status}`}>{job.status}</span>
                </div>
                {job.errorMessage ? <p className="error">{job.errorMessage}</p> : null}
                {job.status === "succeeded" ? (
                  <div className="row">
                    <button
                      type="button"
                      onClick={() => downloadMutation.mutate(job.jobId)}
                      disabled={busy}
                    >
                      Download EPUB
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
